import React, { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { api } from "@/lib/api";
import PartnerLandingRenderer from "@/components/PartnerLandingRenderer";
import { AlertTriangle, ArrowRight } from "lucide-react";

/**
 * Fase 13.3 · Renderer público de la landing de un partner.
 * Ruta: `/l/:slug` (opcional `?preview=TOKEN`).
 * Nav minimalista: logo del partner + link a catálogo del tenant.
 */
export default function PartnerLanding() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const previewToken = params.get("preview") || "";
  const [landing, setLanding] = useState(null);
  const [programs, setPrograms] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const url = previewToken
          ? `/public/landing/${slug}?preview=${encodeURIComponent(previewToken)}`
          : `/public/landing/${slug}`;
        const r = await api.get(url);
        setLanding(r.data);
        // Cargar programas referenciados en `programs_showcase`
        const slugs = new Set();
        for (const b of r.data.blocks || []) {
          if (b.type === "programs_showcase") {
            (b.props?.program_slugs || []).forEach((s) => slugs.add(s));
          }
        }
        if (slugs.size) {
          const arr = Array.from(slugs);
          const map = {};
          await Promise.all(arr.map(async (s) => {
            try {
              const pr = await api.get(`/catalog/programs/${s}`);
              map[s] = pr.data;
            } catch (_) {}
          }));
          setPrograms(map);
        }
      } catch (e) {
        setError(e.response?.data?.detail?.code || "load_failed");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug, previewToken]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Cargando…</div>;
  }

  if (error) {
    return <NotAvailable slug={slug} code={error} />;
  }

  const branding = { ...(landing.tenant_branding || {}), ...(landing.branding_override || {}) };
  const primary = branding.primary_color || "#DC5A0F";

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{landing.seo?.title || `${landing.tenant_name} · Academia`}</title>
        <meta name="description" content={landing.seo?.description || ""} />
        {landing.seo?.og_image_url && <meta property="og:image" content={landing.seo.og_image_url} />}
      </Helmet>

      {/* Nav mínima */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to={`/l/${slug}`} className="flex items-center gap-3" data-testid="partner-landing-nav-logo">
            {branding.logo_url ? (
              <img src={branding.logo_url} alt={landing.tenant_name} className="h-8 w-8 object-contain rounded" />
            ) : (
              <div className="h-8 w-8 rounded flex items-center justify-center text-white font-bold text-sm" style={{ backgroundColor: primary }}>
                {(landing.tenant_name || "?").slice(0, 1).toUpperCase()}
              </div>
            )}
            <span className="font-bold text-slate-900">{landing.tenant_name}</span>
          </Link>
          <Link
            to={`/t/${slug}/catalogo`}
            data-testid="partner-landing-nav-catalog"
            className="text-sm font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
          >
            Ver catálogo <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </header>

      {landing.is_preview && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900 px-4 py-2 text-center text-xs font-bold uppercase tracking-widest" data-testid="preview-banner">
          Vista previa · borrador no publicado
        </div>
      )}

      <PartnerLandingRenderer landing={landing} programs={programs} />

      <footer className="border-t border-slate-200 bg-slate-50 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between text-xs text-slate-500 flex-wrap gap-3">
          <p>© {new Date().getFullYear()} {landing.tenant_name} · en alianza con Debt Capital College</p>
          <span className="uppercase tracking-widest font-semibold text-amber-700">Portal en modo demo</span>
        </div>
      </footer>
    </div>
  );
}

function NotAvailable({ slug, code }) {
  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="mx-auto w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
          <AlertTriangle className="w-6 h-6 text-slate-500" />
        </div>
        <h1 className="mt-5 text-2xl font-bold text-slate-900">Esta landing aún no está publicada</h1>
        <p className="mt-2 text-slate-600 text-sm">
          El partner <span className="font-mono">{slug}</span> todavía no publica su página, o el enlace de preview expiró.
        </p>
        <Link to="/catalogo" className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-primary hover:brightness-110 text-white text-sm font-semibold">
          Explorar catálogo DCC <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
